import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { COMMUNITY_TABS, SubMenuService } from '../../../../services/submenu.service';
import { Subscription, skip } from 'rxjs';
import { WatchComponent } from './components/watch/watch.component';
import { DockmatesComponent } from './components/dockmates/dockmates.component';
import { FleetsComponent } from './components/fleets/fleets.component';
import { TripsComponent } from './components/trips/trips.component';
import { WaypointsComponent } from './components/waypoints/waypoints.component';
import { SettingsComponent } from './components/settings/settings.component';
import { VesselsComponent } from './components/vessels/vessels.component';
import { AuthService } from '../../../../core/auth/auth.service';
import { VesselService } from '../../../../services/vessel.service';
import { ExitButtonComponent } from '../../../../shared/components/buttons/exit-button/exit-button.component';
import { CameraIconButtonComponent } from '../../../../shared/components/buttons/camera-button/camera-button.component';
import { DockmateFullProfilePageComponent } from '../../../dockmate-full-profile-page/dockmate-full-profile-page.component';
import { FleetDetailComponent } from './components/fleets/components/fleet-detail/fleet-detail.component';
import { TourAnchorDirective } from '../../../../features/learning-center/engine/tour-anchor.directive';
import { LearningCenterService } from '../../../../features/learning-center/engine/learning-center.service';
import { PhotosComponent } from './components/photos/photos.component';
import { PostsComponent } from './components/posts/posts.component';

const VALID_TABS =['watch', 'dockmates', 'fleets', 'trips', 'waypoints', 'photos', 'posts', 'vessels', 'settings'];

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [
    CommonModule,
    WatchComponent,
    DockmatesComponent,
    FleetsComponent,
    TripsComponent,
    WaypointsComponent,
    PhotosComponent,
    PostsComponent,
    SettingsComponent,
    VesselsComponent,
    ExitButtonComponent,
    CameraIconButtonComponent,
    DockmateFullProfilePageComponent,
    FleetDetailComponent,
    TourAnchorDirective,
  ],
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.scss',
})
export class UserProfileComponent implements OnInit, OnDestroy {
  isOpen = false;
  private _activeTab = 'watch';
  get activeTab(): string { return this._activeTab; }
  set activeTab(tab: string) {
    this._activeTab = tab;
    this.submenuService.setActiveProfileTab(tab);
  }
  /** Flutter's ProfileSurface.community: Watch / Captains / Fleets, no profile header. */
  get isCommunity(): boolean { return COMMUNITY_TABS.includes(this._activeTab); }
  isHeaderCollapsed = false;
  initialSubTab: string | null = null;
  initialId: string | null = null;
  private subscription: Subscription = new Subscription();
  profileImage: string | null = null;
  userProfile: any = null;
  isLoading = false;
  nestedActiveTab: string | null = null;

  showFullProfileOverlay :any;
  selectedFullProfileId: string | null = null;
  selectedFullProfileObject: any = null;
  selectedSidebarFleet: any = null;
  @ViewChild(DockmatesComponent) dockMate!: DockmatesComponent;

  constructor(
    private submenuService: SubMenuService,
    private authService: AuthService,
    private vesselService: VesselService,
    private location: Location,
    private learningCenter: LearningCenterService,
  ) { }

  /** Opens the Learning Center (revisitable list of guided feature tours). */
  openLearningCenter(): void {
    this.learningCenter.open();
  }

  ngOnInit() {
      this.submenuService.showDockmateOverlay$
    .subscribe((show) => {
      this.showFullProfileOverlay = show;
    });
    // Restore tab from URL if user landed on /:tab
    const segments = window.location.pathname.split('/').filter(Boolean);
    if (segments[0] && VALID_TABS.includes(segments[0])) {
      this.activeTab = segments[0];

      const dockmatesSubTabs = ['find', 'my-dockmates', 'incoming', 'sent'];
      const fleetsSubTabs = ['find', 'my-fleets', 'incoming', 'sent'];

      if (segments[1]) {
        if (this.activeTab === 'dockmates' && dockmatesSubTabs.includes(segments[1])) {
          this.initialSubTab = segments[1];
          this.initialId = segments[2] || null;
        } else if (this.activeTab === 'fleets' && fleetsSubTabs.includes(segments[1])) {
          this.initialSubTab = segments[1];
          this.initialId = segments[2] || null;
        } else {
          this.initialId = segments[1];
        }
      }
    }

    // Listen for cross-component tab navigation (e.g. fleet detail → trips)
    this.subscription.add(
      this.submenuService.profileTabNavigation$.subscribe(({ tab, id }) => {
        this.activeTab = tab;
        this.initialSubTab = null;
        // Reset to null first so Angular always detects a change and fires
        // ngOnChanges in the child tab component, even if the same ID is re-emitted.
        this.initialId = null;
        this.location.replaceState('/' + tab + (id ? '/' + id : ''));
        if (id) {
          setTimeout(() => { this.initialId = id; }, 0);
        }
      })
    );

    // A captain's profile requested from elsewhere (e.g. the map boat card's owner row)
    this.subscription.add(
      this.submenuService.captainProfileRequest$.subscribe((req) => this.onOpenFullProfile(req)),
    );

    // Listen for profile open/close from the service
    this.subscription.add(
      this.submenuService.userProfileOpenState$.subscribe((state) => {
        setTimeout(() => {
          this.isOpen = state;
          if (state) {
            document.body.style.overflow = 'hidden';
            if (!this.userProfile) {
              this.fetchProfile();
            }
            // Update browser URL bar without triggering route navigation
            this.location.replaceState('/' + this.activeTab);
          } else if (!this.submenuService.profileBeingOpened) {
            document.body.style.overflow = '';
            // Reset profile state so the next account's data is fetched fresh on re-open
            this.userProfile = null;
            this.activeTab = 'watch';
            // Only go back to /home on a genuine close (not the transient false during open)
            if (VALID_TABS.some(t => window.location.pathname.startsWith('/' + t))) {
              this.location.replaceState('/home');
            }
          }
        }, 10);
      })
    );

    // Listen for primary vessel change to refresh header
    this.subscription.add(
      this.vesselService.primaryVesselChanged$.subscribe(() => {
        this.fetchProfile();
      })
    );

    // When a new account logs in, reset all panel state so every child component
    // is destroyed and recreated with the new user's data (skip(1) ignores the
    // initial BehaviorSubject emission at subscribe time).
    this.subscription.add(
      this.authService.isLoggedIn$.pipe(skip(1)).subscribe((isLoggedIn) => {
        if (isLoggedIn) {
          this.userProfile = null;
          this.activeTab = 'watch';
          if (this.isOpen) {
            this.fetchProfile();
          }
        }
      })
    );
  }

  fetchProfile() {
    const userId = this.authService.getUserIdFromToken();
    if (!userId) return;

    this.isLoading = true;
    this.authService.getUserProfile(userId).subscribe({
      next: (res) => {
        this.userProfile = res;
        this.profileImage = res.image_url || null;
        if (res._id) {
          this.authService.setUserObjectId(res._id);
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching profile:', err);
        this.isLoading = false;
      },
    });
  }

  getName(first_name: string, last_name: string) {
    if (first_name.length === 0 && last_name.length === 0) return 'User';
    return (
      first_name.charAt(0).toUpperCase() +
      first_name.slice(1) +
      ' ' +
      last_name.charAt(0).toUpperCase() +
      last_name.slice(1)
    );
  }

  getInitials(): string {
    if (!this.userProfile) return '';

    const first = this.userProfile.first_name?.charAt(0) || '';
    const last = this.userProfile.last_name?.charAt(0) || '';

    if (first || last) {
      return (first + last).toUpperCase();
    }

    const names = (this.userProfile.display_name || '').trim().split(/\s+/);
    if (names.length >= 2) {
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    }

    return (names[0]?.charAt(0) || '?').toUpperCase();
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files[0]) return;

    this.isLoading = true;
    const userId = this.userProfile?._id;

    if (userId) {
      this.authService.updateProfileImage(userId, input.files[0]).subscribe({
        next: (res) => {
          this.userProfile = res;
          this.profileImage = res.image_url;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error updating profile image:', err);
          this.isLoading = false;
        },
      });
    } else {
      const reader = new FileReader();
      reader.onload = () => {
        this.profileImage = reader.result as string;
        this.isLoading = false;
      };
      reader.readAsDataURL(input.files[0]);
    }
  }

  syncDockmatesList(data:any) {
    this.dockMate.syncListItem(data);
  }

  onOpenFullProfile(event: { profileId: string; profileObject: any , activeTab?: string}) {
    this.selectedFullProfileId = event.profileId;
    this.selectedFullProfileObject = event.profileObject;
    this.submenuService.setDockmateAndFleetOverlay(true);
    this.nestedActiveTab = event.activeTab || null;
  }

  closeFullProfileOverlay() {
    this.submenuService.setDockmateAndFleetOverlay(false);
    this.selectedFullProfileId = null;
    this.selectedFullProfileObject = null;
  }

  openVesselsTab() {
    this.closeFullProfileOverlay();
    this.setActiveTab('vessels');
  }

  onSettingsProfileUpdated(updatedProfile: any) {
    this.userProfile = { ...(this.userProfile || {}), ...(updatedProfile || {}) };
    if (updatedProfile?.image_url) {
      this.profileImage = updatedProfile.image_url;
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    document.body.style.overflow = '';
  }

  closeProfile() {
    this.isOpen = false;
    document.body.style.overflow = '';
    this.submenuService.setUserProfileOpenState(false);
  }

  setActiveTab(tab: string) {
    this.activeTab = tab;
    this.initialSubTab = null;
    this.initialId = null;
    this.location.replaceState('/' + tab);
    // The whole profile container scrolls now (header scrolls away, tab row is
    // sticky), so reset its scroll position when switching tabs.
    const scroller = document.querySelector('.user-profile-container') as HTMLElement;
    if (scroller) scroller.scrollTop = 0;
  }

  onContentScroll(event: Event) {
    const el = event.target as HTMLElement;
    const scrollPos = el.scrollTop;

    // Only collapse if we have enough content to fill the screen even when collapsed
    // This prevents the "shaking" loop where collapsing makes the container too tall for the content
    const collapseThreshold = 60;
    const expandThreshold = 20;

    // We need at least the visible height + some buffer to justify collapsing
    // Reducing buffer from 150 to 50 to make it feel less "stuck"
    const isScrollableEnough = el.scrollHeight > el.clientHeight + 50;

    if (!this.isHeaderCollapsed && scrollPos > collapseThreshold && isScrollableEnough) {
      this.isHeaderCollapsed = true;
    } else if (this.isHeaderCollapsed && scrollPos < expandThreshold) {
      this.isHeaderCollapsed = false;
    }
  }
}
function takeUntilDestroyed(destroyRef: any): any {
  throw new Error('Function not implemented.');
}

